import { z } from 'zod';
import { cursorPageQuerySchema } from './pagination.js';
// `Food.allergens` reuses the onboarding `Allergen` enum deliberately — the
// schema's own note says so, precisely so a user's allergies compare against a
// food without a translation layer in between. One tuple, both meanings.
import { ALLERGENS } from './user-detail.js';
/**
 * The food catalog.
 *
 * This is the first admin surface that mutates, so two rules from the design
 * spec stop being theoretical here:
 *
 * - **`support` may read the catalog and change nothing.** Every mutating
 *   request below is `admin` only, enforced by `requireRole` on the route.
 * - **There are no hard deletes.** Deactivating clears `isActive`; merging
 *   deactivates the duplicate rather than removing it. Meal logs point at food
 *   rows historically and a catalog row must never vanish under one (spec §6).
 */
export const FOOD_KINDS = ['ingredient', 'dish'];
export const FOOD_SOURCES = ['indb', 'ifct', 'open_food_facts', 'ai_estimated', 'manual', 'gym'];
export const FOOD_DIET_CLASSES = ['vegan', 'vegetarian', 'eggetarian', 'non_vegetarian'];
/**
 * Who decided a food's allergen list, and therefore what an empty one is worth.
 *
 * Rendered on the catalog screen because it is the difference between "this
 * food contains none of the six" and "nobody has ever looked" — two facts that
 * were spelled the same way until Unit 8, which is how every INDB dish
 * disappeared from the shortlist of any user with an allergy.
 */
export const ALLERGEN_PROVENANCES = ['unassessed', 'source_stated', 'ingredient_derived'];
/**
 * Who decided a food's diet class, mirroring `DietClassProvenance` in
 * schema.prisma. `name_inferred` is a guess read off the dish's own name and
 * is excluded from what a recipe may derive from (see recipe-derivation.ts);
 * it is still a value a food can carry, so it belongs in this tuple.
 */
export const DIET_CLASS_PROVENANCES = [
    'source_stated',
    'name_inferred',
    'ingredient_derived',
    'reviewed',
];
/** Who wrote a recipe's lines, mirroring `RecipeSource` in schema.prisma. */
export const RECIPE_SOURCES = ['llm_decomposition', 'agent_research', 'manual'];
/** Mirrors `RecipeStatus`. A dish's detail only ever shows the ACTIVE one. */
export const RECIPE_STATUSES = ['draft', 'active', 'retired'];
/** Mirrors `RecipeConfidence` — the author's own confidence in the lines. */
export const RECIPE_CONFIDENCES = ['low', 'medium', 'high'];
/**
 * The closed cuisine vocabulary, mirroring src/modules/foods/cuisines.ts in
 * the API (the CUISINE_AGREEMENT check there proves the two agree).
 * "universal" is the catalog-side tag for staples every regional table
 * shares; user preferences never carry it.
 */
export const FOOD_CUISINES = [
    'north_indian',
    'south_indian',
    'east_indian',
    'west_indian',
    'bengali',
    'gujarati',
    'maharashtrian',
    'punjabi',
    'rajasthani',
    'chinese_indian',
    'continental',
    'universal',
];
/**
 * What a food anchors a meal with. Null on the row is "uncurated", rendered
 * as absent rather than a guessed member — see `Food.mealRole` in
 * schema.prisma. Edited in the console or pushed with `catalog:push`.
 */
export const MEAL_ROLES = [
    'main',
    'protein_anchor',
    'staple',
    'vegetable',
    'side',
    'snack',
    'beverage',
    'condiment',
    'dessert',
];
/**
 * How long a food takes to cook, when somebody has an opinion — see
 * `Food.prepTimeBand` in schema.prisma. Null is "uncurated". Edited in the
 * console or pushed with `catalog:push`.
 */
export const PREP_TIME_BANDS = ['under_10', 'under_30', 'over_30'];
/**
 * What a food typically costs, when somebody has an opinion — see
 * `Food.costBand` in schema.prisma. Same null posture as `prepTimeBand`.
 */
export const COST_BANDS = ['low', 'moderate', 'premium'];
/**
 * How a food sits in an everyday diet, mirroring `HealthTier` (and
 * `HEALTH_TIERS` in src/modules/foods/health-tier.ts). Null is "uncurated".
 */
export const HEALTH_TIERS = ['everyday', 'moderate', 'treat'];
/**
 * The occasions a food suits, mirroring `MEAL_TIMES` in
 * src/modules/foods/meal-times.ts (the `MEAL_TIME_AGREEMENT` check in
 * admin-foods.service.ts proves the two agree). A TS tuple rather than a
 * database enum on both sides: the column is TEXT[] with a CHECK, so the
 * vocabulary grows without a migration.
 *
 * An empty list is "nobody has looked", and `fitsSlot` reads it as fitting
 * every slot — so leaving it empty never removes a food from a plan.
 */
export const FOOD_MEAL_TIMES = [
    'breakfast',
    'mid_morning',
    'lunch',
    'snack',
    'dinner',
    'pre_workout',
];
/**
 * How many household portions the plan solver may serve of this food — the
 * role default from `src/modules/foods/serving-policy.ts`, or a per-food
 * override. `maxDayPortions` is the only field nullable *within* a stated
 * policy (no day-level cap); the other four are all set together or not at
 * all — see `Food.servingMinPortions` in schema.prisma for the DB's own
 * all-or-none CHECK.
 */
export const adminServingPolicySchema = z.object({
    minPortions: z.number(),
    typicalPortions: z.number(),
    maxPortions: z.number(),
    maxDayPortions: z.number().nullable(),
    elastic: z.boolean(),
});
/**
 * The same five fields, as a curator writes them. The ordering invariants
 * mirror the DB's CHECK (and `servingPolicyEntryProblems` in
 * the catalog engine's catalog-entry.schema.ts) so a bad request 400s here rather than surfacing as
 * a raw constraint violation.
 */
export const adminServingPolicyRequestSchema = z
    .object({
    minPortions: z.number().positive().max(10),
    typicalPortions: z.number().positive().max(10),
    maxPortions: z.number().positive().max(10),
    maxDayPortions: z.number().positive().max(20).nullable(),
    elastic: z.boolean(),
})
    .refine((policy) => policy.minPortions <= policy.typicalPortions, {
    message: 'minPortions must be <= typicalPortions.',
})
    .refine((policy) => policy.typicalPortions <= policy.maxPortions, {
    message: 'typicalPortions must be <= maxPortions.',
})
    .refine((policy) => policy.maxDayPortions === null || policy.maxDayPortions >= policy.maxPortions, {
    message: 'maxDayPortions must be >= maxPortions when set.',
});
/**
 * The most energy 100 g of any food can carry: nothing is denser than pure
 * fat, and the two IFCT oils sit at 900.09. The catalog's nutrition rules
 * hold every write to it; the console's editor states the same bound. Mirrors
 * `MAX_KCAL_PER_100G` (`src/modules/foods/nutrition-bounds.ts`), and
 * `admin-food-edit.ts` proves the two agree at compile time.
 */
export const FOOD_MAX_KCAL_PER_100G = 905;
/** Per 100 g edible portion — the contract every calculation in the product uses. */
export const foodMacrosSchema = z.object({
    kcal: z.number(),
    proteinG: z.number(),
    carbsG: z.number(),
    fatG: z.number(),
    /** Null rather than zero: many foods genuinely have no fibre figure. */
    fibreG: z.number().nullable(),
});
export const adminFoodListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    kind: z.enum(FOOD_KINDS),
    source: z.enum(FOOD_SOURCES),
    category: z.string().nullable(),
    dietClass: z.enum(FOOD_DIET_CLASSES),
    hasOnionGarlic: z.boolean(),
    jainExcluded: z.boolean(),
    allergens: z.array(z.enum(ALLERGENS)),
    /** What `allergens` above is actually worth — see ALLERGEN_PROVENANCES. */
    allergenSource: z.enum(ALLERGEN_PROVENANCES),
    macrosPer100g: foodMacrosSchema,
    /**
     * Whether an Indian household can cook or order this for everyday eating.
     * The plan shortlist reads it as a demotion key (never an exclusion, and
     * search ignores it); seeded by regex + an LLM pass, edited here.
     */
    isCommonInIndia: z.boolean(),
    /**
     * Regional tags from FOOD_CUISINES. Another shortlist ranking input, same
     * demote-not-exclude shape; empty means unclassified, which ranks like
     * universal. Stamped by the classifier script, edited here.
     */
    cuisines: z.array(z.enum(FOOD_CUISINES)),
    /** What this food anchors a meal with. Null means uncurated — see MEAL_ROLES. */
    mealRole: z.enum(MEAL_ROLES).nullable(),
    /** How long this food takes to cook. Null means uncurated — see PREP_TIME_BANDS. */
    prepTimeBand: z.enum(PREP_TIME_BANDS).nullable(),
    /** What this food typically costs. Null means uncurated — see COST_BANDS. */
    costBand: z.enum(COST_BANDS).nullable(),
    isVerified: z.boolean(),
    isActive: z.boolean(),
    aliasCount: z.number().int(),
    portionCount: z.number().int(),
    createdAt: z.string(),
});
/**
 * `verification` and `activity` are tri-state rather than booleans: "either"
 * has to be expressible, and an absent boolean query parameter is
 * indistinguishable from `false` once it has been through a query string.
 */
export const FOOD_VERIFICATION_FILTERS = ['verified', 'unverified'];
export const FOOD_ACTIVITY_FILTERS = ['active', 'inactive'];
/**
 * Oldest-first exists for the review queue, which is a queue: the row waiting
 * longest is the one to look at next. The cursor comparison flips with it.
 */
export const FOOD_ORDERS = ['newest', 'oldest'];
/**
 * Tri-state like the two above. `without` is the curation queue that matters:
 * a portionless ingredient ranks last in the plan shortlist and reaches a
 * plan only as a 100 g fallback, so "ingredients without one" is the list of
 * foods a curator can promote by filling in a katori or a piece.
 */
export const FOOD_HOUSEHOLD_PORTION_FILTERS = ['with', 'without'];
export const adminFoodListQuerySchema = cursorPageQuerySchema.extend({
    /** Matches the food's name or any of its aliases, including Indian-language ones. */
    search: z.string().trim().max(120).optional(),
    kind: z.enum(FOOD_KINDS).optional(),
    source: z.enum(FOOD_SOURCES).optional(),
    dietClass: z.enum(FOOD_DIET_CLASSES).optional(),
    mealRole: z.enum(MEAL_ROLES).optional(),
    prepTimeBand: z.enum(PREP_TIME_BANDS).optional(),
    costBand: z.enum(COST_BANDS).optional(),
    verification: z.enum(FOOD_VERIFICATION_FILTERS).optional(),
    activity: z.enum(FOOD_ACTIVITY_FILTERS).optional(),
    householdPortion: z.enum(FOOD_HOUSEHOLD_PORTION_FILTERS).optional(),
    order: z.enum(FOOD_ORDERS).default('newest'),
});
// --- Detail (the drawer) ----------------------------------------------------
export const foodAliasSchema = z.object({
    id: z.string().uuid(),
    alias: z.string(),
    /** BCP-47 where known ("hi", "ta"), null for an English variant. */
    language: z.string().nullable(),
});
/**
 * The closed household-measure vocabulary, mirroring Prisma's `PortionUnit`.
 * The unit governs how the plan solver may size an item (half-steps for the
 * ladled units, whole/half pieces for counted ones, continuous only for
 * `grams`); the label stays display text.
 */
export const PORTION_UNITS = [
    'katori',
    'bowl',
    'plate',
    'glass',
    'cup',
    'piece',
    'slice',
    'tbsp',
    'tsp',
    'handful',
    'serving',
    'grams',
];
export const foodPortionSchema = z.object({
    id: z.string().uuid(),
    label: z.string(),
    unit: z.enum(PORTION_UNITS),
    grams: z.number(),
    isDefault: z.boolean(),
});
/**
 * One recipe line. `ingredientFoodId` is nullable because the decomposition
 * tier keeps the model's raw ingredient name even when it matched nothing —
 * losing the line would lose the decomposition, and an unresolved line is
 * exactly what the AI-operations screen will later count.
 */
export const foodIngredientSchema = z.object({
    id: z.string().uuid(),
    rawName: z.string(),
    ingredientFoodId: z.string().uuid().nullable(),
    ingredientName: z.string().nullable(),
    grams: z.number(),
    retentionGroup: z.string().nullable(),
});
/**
 * The dish's current ACTIVE recipe, or null when it has none. `reviewer` is
 * null for an unreviewed `llm_decomposition` recipe, and is exactly what
 * withholds a diet-class derivation from it — see recipe-derivation.ts.
 */
export const foodRecipeSchema = z.object({
    version: z.number().int(),
    source: z.enum(RECIPE_SOURCES),
    status: z.enum(RECIPE_STATUSES),
    coverage: z.number(),
    confidence: z.enum(RECIPE_CONFIDENCES),
    reviewer: z.string().nullable(),
    lines: z.array(foodIngredientSchema),
});
/**
 * How much this row is load-bearing, which is what makes deactivating or
 * merging it a decision rather than a click. These are counts of *history*:
 * they are the reason no row here is ever hard-deleted.
 */
export const foodUsageSchema = z.object({
    mealLogItems: z.number().int(),
    scans: z.number().int(),
    usedInDishes: z.number().int(),
});
export const adminFoodDetailSchema = adminFoodListItemSchema.extend({
    sourceId: z.string().nullable(),
    updatedAt: z.string(),
    /** What the app shows instead of `name`; null when the catalog name already reads well. */
    displayName: z.string().nullable(),
    /** Whether a person eats this as it is. False keeps it out of plans, never out of search. */
    servableAsIs: z.boolean(),
    /** Empty means nobody has looked, which fits every slot. */
    mealTimes: z.array(z.enum(FOOD_MEAL_TIMES)),
    /** Months in season, ascending. Empty means nobody has looked: available all year. */
    seasonMonths: z.array(z.number().int()),
    /** Null means uncurated — see HEALTH_TIERS. */
    healthTier: z.enum(HEALTH_TIERS).nullable(),
    /** Who decided `dietClass` above. Null means nobody has — see decision #47. */
    dietClassSource: z.enum(DIET_CLASS_PROVENANCES).nullable(),
    aliases: z.array(foodAliasSchema),
    portions: z.array(foodPortionSchema),
    /** Null means this dish has no recipe at all yet. */
    recipe: foodRecipeSchema.nullable(),
    usage: foodUsageSchema,
    /** The curator-set override, or null when this food rides the role default. */
    servingPolicy: adminServingPolicySchema.nullable(),
    /**
     * What the plan solver actually uses right now — `servingPolicy` when set,
     * otherwise the resolved role default (or, for an uncurated food, the raw
     * unit grid). Always present, so the operator sees the real number in
     * effect rather than having to work the fallback out by hand.
     */
    effectiveServingPolicy: adminServingPolicySchema,
});
// --- Mutations --------------------------------------------------------------
export const adminFoodParamsSchema = z.object({
    id: z.string().uuid(),
});
/**
 * Merging folds a duplicate into the row that survives.
 *
 * `intoFoodId` is the survivor. What moves and what deliberately does not is
 * the whole of the operation's meaning — see `admin-foods.repository.ts`.
 */
export const adminFoodMergeRequestSchema = z.object({
    intoFoodId: z.string().uuid(),
});
/**
 * A reason is required, not optional. Deactivating a catalog row changes what
 * every user can log; "why" belongs in the audit row while the operator still
 * remembers it, not reconstructed from a timestamp six months later.
 */
export const adminFoodDeactivateRequestSchema = z.object({
    reason: z.string().trim().min(3).max(280),
});
/**
 * Verifying states whether everyday Jain practice avoids the food (a root or
 * bulb, a mushroom, honey, a fig, alcohol, or a dish made with one).
 *
 * Required, not optional. A verified AI-estimated row becomes plannable, and
 * `jainExcluded` defaults to false, so a verify that said nothing would put
 * the food on Jain plans without anyone having decided it belongs there.
 *
 * `dietClass` is the operator's confirmed diet class, written as REVIEWED.
 * Required for a photo scan's estimate (source `ai_estimated`), whose class
 * was only ever provisional: worked out from the model's ingredient list, or
 * NON_VEGETARIAN when that list did not resolve. Optional for anything else,
 * whose class came from its source.
 */
export const adminFoodVerifyRequestSchema = z.object({
    jainExcluded: z.boolean(),
    dietClass: z.enum(FOOD_DIET_CLASSES).optional(),
});
// --- Portions ---------------------------------------------------------------
export const adminFoodPortionParamsSchema = z.object({
    id: z.string().uuid(),
    portionId: z.string().uuid(),
});
/**
 * One household measure, written by a curator.
 *
 * Giving a food a real portion is what promotes it in the plan shortlist: an
 * ingredient with no non-gram unit ranks last and reaches a plan only as a
 * 100 g fallback. The label is a bare singular noun ("katori", "roti") —
 * clients render it against a count, so a label carrying its own count would
 * read twice.
 */
export const adminFoodPortionRequestSchema = z.object({
    label: z
        .string()
        .trim()
        .min(1)
        .max(80)
        .regex(/^[^0-9]/, 'A label is a bare noun — the count is rendered by the client.'),
    unit: z.enum(PORTION_UNITS),
    grams: z.number().positive().max(5000),
    isDefault: z.boolean().default(false),
});
// --- Recipe ------------------------------------------------------------------
/**
 * One recipe line, as an operator writes it. Bounds mirror the DB columns
 * (`rawName` VARCHAR(160), `retentionGroup` VARCHAR(48)).
 * `ingredientFoodId: null` is a legal, deliberately unresolved line, the same
 * as an LLM decomposition's unmatched name — it correctly refuses coverage.
 */
export const adminFoodRecipeLineRequestSchema = z.object({
    rawName: z.string().trim().min(1).max(160),
    ingredientFoodId: z.string().uuid().nullable(),
    grams: z.number().positive().max(5000),
    retentionGroup: z.string().trim().min(1).max(48).nullable(),
});
/**
 * Replaces a dish's recipe. Always mints a new MANUAL, ACTIVE version and
 * retires the current one, even when the lines are unchanged — this is an
 * explicit "this is now the resource" action, not a converge-if-different
 * script (see the catalog engine's `sameRecipe`, which this endpoint
 * deliberately does not reuse).
 */
export const adminFoodRecipeUpdateRequestSchema = z.object({
    lines: z.array(adminFoodRecipeLineRequestSchema).min(1).max(60),
    confidence: z.enum(RECIPE_CONFIDENCES),
    note: z.string().trim().max(2000).optional(),
});
// --- Create ------------------------------------------------------------------
/**
 * The two allergen provenances an operator may write, and the reason this is a
 * choice on the form rather than a value the server picks.
 *
 * `source_stated` is what tells the plan shortlist an allergen list is
 * trustworthy; `unassessed` is withheld from a user with a recorded allergy
 * precisely because an empty list would otherwise read as an all-clear (see
 * `Food.allergenSource` in schema.prisma). If create stamped `source_stated`
 * unconditionally, an operator who accepted an LLM draft without reading it
 * would have laundered a model's guess into the value that means *the source
 * said so* — and nothing in this product asks a model whether a food is
 * nut-free (recipe-derivation.ts). So the operator ticks it, and a research
 * draft never carries one.
 *
 * `ingredient_derived` is absent on purpose: derivation from a recipe writes
 * it, never a person.
 */
export const ALLERGEN_WRITE_PROVENANCES = ['unassessed', 'source_stated'];
/** One alias, as a curator writes it. `language` is BCP-47, null for English. */
export const adminFoodAliasRequestSchema = z.object({
    alias: z.string().trim().min(1).max(160),
    language: z.string().trim().min(2).max(12).nullable(),
});
/** A dish's recipe as it is written at creation: version 1, MANUAL, ACTIVE. */
export const adminFoodRecipeCreateSchema = z.object({
    lines: z.array(adminFoodRecipeLineRequestSchema).min(1).max(60),
    confidence: z.enum(RECIPE_CONFIDENCES),
    note: z.string().trim().max(2000).optional(),
});
/** Plain research prose only: source links do not belong in the form or saved notes. */
export function stripResearchLinks(text) {
    // Providers sometimes encode a source before returning it as prose. Decode
    // locally, with a bounded number of passes, so the same removal rules apply.
    for (let pass = 0; pass < 4; pass += 1) {
        const decoded = text
            .replace(/\\u([0-9a-f]{4})/gi, (_, hex) => String.fromCharCode(Number.parseInt(hex, 16)))
            .replace(/&#(x[0-9a-f]+|\d+);?/gi, (match, code) => {
            const value = code.toLowerCase().startsWith('x')
                ? Number.parseInt(code.slice(1), 16)
                : Number.parseInt(code, 10);
            return value <= 0x10ffff ? String.fromCodePoint(value) : match;
        })
            .replace(/&(amp|colon|sol|period|percnt);/gi, (_, entity) => {
            const entities = { amp: '&', colon: ':', sol: '/', period: '.', percnt: '%' };
            return entities[entity.toLowerCase()] ?? '';
        })
            .replace(/(?:%[0-9a-f]{2})+/gi, (encoded) => {
            try {
                return decodeURIComponent(encoded);
            }
            catch {
                return encoded;
            }
        });
        if (decoded === text)
            break;
        text = decoded;
    }
    return text
        .replace(/^\s{0,3}\[[^\]\r\n]+\]:[^\r\n]*(?:\r?\n|$)/gm, '')
        .replace(/!?\[([^\]\r\n]*)\]\((?:[^()\r\n]|\([^()\r\n]*\))*\)/g, '$1')
        .replace(/!?\[([^\]\r\n]*)\]\[[^\]\r\n]*\]/g, '$1')
        .replace(/\b(?:https?:\/\/|www\.)[^\s<>"'[\]{}]+/gi, '')
        .replace(/\/\/[^\s<>"'[\]{}]+/g, '')
        .replace(/\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63})(?::\d{1,5})?(?:[/?#][^\s<>"'[\]{}]*)?/gi, '')
        .replace(/<\s*>|\(\s*\)|\[\s*\]/g, '')
        .replace(/[ \t]+/g, ' ')
        .replace(/ +([,.;:!?])/g, '$1')
        .replace(/ +\n/g, '\n')
        .trim();
}
/** Describes the research draft, never an attestation of later operator edits. */
export const adminFoodResearchMetadataSchema = z.object({
    nutritionBasis: z.enum(['published', 'calculated', 'estimated', 'unresolved']),
    portionBasis: z.enum(['published', 'calculated', 'estimated', 'unresolved']),
    foodState: z.enum(['raw', 'cooked', 'as_sold', 'unknown']),
    summary: z.string().trim().max(1000).transform(stripResearchLinks),
    assumptions: z.array(z.string().trim().max(400).transform(stripResearchLinks)).max(8),
});
/**
 * A new catalog row, whole, in one request.
 *
 * Everything a food *is* is here; everything about where it came from is
 * derived server-side and cannot be supplied. `slug` is built from the name
 * and frozen — it is a stable key other rows reference, and two operators
 * typing one for the same food is how a duplicate gets in. `source`,
 * `sourceId`, `nameNormalised`, `dietClassSource`, `isActive` and `isVerified`
 * are the server's to write too; see `POST /admin/foods`.
 *
 * `isVerified` is deliberately absent rather than defaulted. The review queue
 * filters on `source: ai_estimated` as well as `verification: unverified`, so
 * a MANUAL row created unverified would sit in no queue at all, permanently,
 * while clients rendered its numbers as an unreviewed estimate. Filling in
 * this form *is* an operator signing off.
 */
export const adminFoodCreateRequestSchema = z
    .object({
    name: z.string().trim().min(2).max(160),
    kind: z.enum(FOOD_KINDS),
    category: z.string().trim().min(1).max(120).nullable(),
    /** Per 100 g edible portion — the contract every calculation uses. */
    kcalPer100g: z.number().nonnegative().max(1000),
    proteinGPer100g: z.number().nonnegative().max(100),
    carbsGPer100g: z.number().nonnegative().max(100),
    fatGPer100g: z.number().nonnegative().max(100),
    fibreGPer100g: z.number().nonnegative().max(100).nullable(),
    dietClass: z.enum(FOOD_DIET_CLASSES),
    hasOnionGarlic: z.boolean(),
    /**
     * Whether everyday Jain practice avoids it. Omitted means not stated: the
     * row lands excluded unless its recipe is complete and clears it, because
     * an unstated answer must not put a food on Jain plans.
     */
    jainExcluded: z.boolean().optional(),
    /** A hard exclusion in search and the shortlist both, so it is stated, never guessed. */
    hasBeef: z.boolean(),
    allergens: z.array(z.enum(ALLERGENS)).max(ALLERGENS.length),
    allergenSource: z.enum(ALLERGEN_WRITE_PROVENANCES),
    /** Whether a person eats this as-is. False removes the row from the shortlist outright. */
    servableAsIs: z.boolean(),
    isCommonInIndia: z.boolean(),
    cuisines: z.array(z.enum(FOOD_CUISINES)).max(4),
    mealRole: z.enum(MEAL_ROLES).nullable(),
    prepTimeBand: z.enum(PREP_TIME_BANDS).nullable(),
    costBand: z.enum(COST_BANDS).nullable(),
    mealTimes: z.array(z.enum(FOOD_MEAL_TIMES)).max(FOOD_MEAL_TIMES.length),
    seasonMonths: z.array(z.number().int().min(1).max(12)).max(12),
    servingPolicy: adminServingPolicyRequestSchema.nullable(),
    aliases: z.array(adminFoodAliasRequestSchema).max(30),
    portions: z.array(adminFoodPortionRequestSchema).max(4),
    /** Dishes only — a recipe is an account of what a dish is made of. */
    recipe: adminFoodRecipeCreateSchema.nullable(),
    /**
     * Whether any of this was filled in from a research draft. It changes
     * nothing about the row and is recorded only in the audit entry: because a
     * created food lands verified, that entry is the sole record of who stood
     * behind the numbers, and "did a person type these or accept them" is most
     * of what someone reading it six months later wants to know.
     */
    researched: z.boolean(),
    /** Optional for older clients; records the basis of the draft the operator reviewed. */
    research: adminFoodResearchMetadataSchema.optional(),
})
    .superRefine((body, ctx) => {
    for (const problem of foodCreateProblems(body)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: problem.path, message: problem.message });
    }
});
/**
 * The cross-field rules, lifted out of the refinement so the console can run
 * the same list and say what is holding Create shut, rather than leaving an
 * operator to guess which of twenty fields a disabled button means.
 */
export function foodCreateProblems(body) {
    const problems = [];
    if (body.portions.length > 0) {
        if (body.portions.filter((portion) => portion.isDefault).length !== 1) {
            problems.push({ path: ['portions'], message: 'Exactly one portion must be the default.' });
        }
        // Case-insensitively, though `@@unique([foodId, label])` is case-sensitive:
        // `portionEntryProblems` in catalog-entry.schema.ts dedupes this way, so
        // "Katori" beside "katori" is a row Postgres accepts and the catalog engine
        // can never converge.
        const labels = body.portions.map((portion) => portion.label.trim().toLowerCase());
        if (new Set(labels).size !== labels.length) {
            problems.push({ path: ['portions'], message: 'Two portions share a label.' });
        }
    }
    if (!isAscendingDistinct(body.seasonMonths)) {
        problems.push({ path: ['seasonMonths'], message: 'Months must be ascending and distinct.' });
    }
    const slots = body.mealTimes.map((slot) => FOOD_MEAL_TIMES.indexOf(slot));
    if (!isAscendingDistinct(slots)) {
        problems.push({ path: ['mealTimes'], message: 'Meal times must be in order and distinct.' });
    }
    if (body.recipe !== null && body.kind !== 'dish') {
        problems.push({ path: ['recipe'], message: 'Only a dish has a recipe.' });
    }
    return problems;
}
/**
 * Ascending and distinct, for the two array columns whose CHECK constraints
 * cannot express it — a CHECK cannot hold the subquery those rules need, so
 * the migrations state the rule and leave enforcing it here.
 */
function isAscendingDistinct(values) {
    return values.every((value, index) => index === 0 || value > (values[index - 1] ?? Number.NEGATIVE_INFINITY));
}
// --- Edit ---------------------------------------------------------------------
/**
 * The five per-100 g figures, written together or not at all: half a
 * nutrition block is how a food ends up with macros that do not add up to its
 * energy. The bounds here are shape only; the rules that matter
 * (`nutritionViolation`, read against the food's meal role) bind on the
 * server, the same ones a pushed batch meets.
 */
export const adminFoodNutritionRequestSchema = z.object({
    kcalPer100g: z.number().nonnegative().max(1000),
    proteinGPer100g: z.number().nonnegative().max(100),
    carbsGPer100g: z.number().nonnegative().max(100),
    fatGPer100g: z.number().nonnegative().max(100),
    /** Null when the source states no fibre figure, which is not zero. */
    fibreGPer100g: z.number().nonnegative().max(100).nullable(),
});
/** One portion of an edited set: `id` names a portion the food has, null a new one. */
export const adminFoodPortionEditSchema = adminFoodPortionRequestSchema.extend({
    id: z.string().uuid().nullable(),
});
/** Names to add, and alias ids to take off. */
export const adminFoodAliasEditSchema = z.object({
    add: z.array(adminFoodAliasRequestSchema).max(30).default([]),
    remove: z.array(z.string().uuid()).max(60).default([]),
});
/** The keys of an edit that change the food; `note` and `sources` only describe one. */
export const FOOD_EDIT_FIELDS = [
    'isCommonInIndia',
    'cuisines',
    'mealRole',
    'servingPolicy',
    'dietClass',
    'hasOnionGarlic',
    'jainExcluded',
    'nutrition',
    'displayName',
    'aliases',
    'allergens',
    'servableAsIs',
    'prepTimeBand',
    'costBand',
    'mealTimes',
    'seasonMonths',
    'healthTier',
    'portions',
];
/**
 * Every fact on a food row the catalog records, edited from the console in
 * one save. Partial: each field replaces what it names only when present, and
 * omitting a key is what "leave alone" means. `null` (or an empty list) is a
 * real write wherever the column has an uncurated state: `mealRole`,
 * `prepTimeBand`, `costBand`, `healthTier`, `mealTimes`, `seasonMonths`, and
 * `servingPolicy`, which clears back to the role default.
 *
 * The whole save is one transaction and one change in History, carrying the
 * operator's name, `note` and `sources`, so it can be reverted there. The
 * server holds it to the rules a pushed batch meets (nutrition bounds against
 * the food's role, portion weights per unit, one default portion, the diet
 * rules allergens imply, meal-time and season order) and refuses with field
 * errors (`error.details[].path`) naming what to change.
 *
 * - `nutrition` writes all five figures.
 * - `portions` is the whole set, in order; a portion left out is removed. The
 *   100 g option is not a portion and is always offered.
 * - `allergens` is the operator's statement, so it writes
 *   `allergenSource: source_stated`.
 * - `dietClass`/`hasOnionGarlic` write `dietClassSource: reviewed`
 *   unconditionally: an operator making the call outranks whatever provenance
 *   was there. Contrast `POST .../recipe/derive`, which never downgrades.
 */
export const adminFoodUpdateRequestSchema = z
    .object({
    isCommonInIndia: z.boolean().optional(),
    cuisines: z.array(z.enum(FOOD_CUISINES)).max(4).optional(),
    mealRole: z.enum(MEAL_ROLES).nullable().optional(),
    servingPolicy: adminServingPolicyRequestSchema.nullable().optional(),
    dietClass: z.enum(FOOD_DIET_CLASSES).optional(),
    hasOnionGarlic: z.boolean().optional(),
    jainExcluded: z.boolean().optional(),
    nutrition: adminFoodNutritionRequestSchema.optional(),
    displayName: z.string().trim().min(1).max(160).optional(),
    aliases: adminFoodAliasEditSchema.optional(),
    allergens: z.array(z.enum(ALLERGENS)).max(ALLERGENS.length).optional(),
    servableAsIs: z.boolean().optional(),
    prepTimeBand: z.enum(PREP_TIME_BANDS).nullable().optional(),
    costBand: z.enum(COST_BANDS).nullable().optional(),
    mealTimes: z.array(z.enum(FOOD_MEAL_TIMES)).max(FOOD_MEAL_TIMES.length).optional(),
    seasonMonths: z.array(z.number().int().min(1).max(12)).max(12).optional(),
    healthTier: z.enum(HEALTH_TIERS).nullable().optional(),
    portions: z.array(adminFoodPortionEditSchema).max(8).optional(),
    /** Why: saved with the change in History, so the next person sees it. */
    note: z.string().trim().min(1).max(2000).optional(),
    /** Where the facts came from: links or citations. */
    sources: z.array(z.string().trim().min(1).max(500)).max(20).optional(),
})
    .refine((body) => FOOD_EDIT_FIELDS.some((field) => body[field] !== undefined), {
    message: 'Nothing to update.',
});
/** Removing one name a food answers to: the alias must belong to the food in the URL. */
export const adminFoodAliasParamsSchema = z.object({
    id: z.string().uuid(),
    aliasId: z.string().uuid(),
});
// --- Research ----------------------------------------------------------------
/**
 * How a drafted recipe line found its catalog ingredient, best first. Mirrors
 * `IngredientMatchTier` in src/modules/foods/ingredient-matcher.ts.
 *
 * `exact` is the name or an alias verbatim; `precedent` is what reviewed
 * recipes pin that raw name to; `closest` is the search ladder's nearest
 * name, verified only by a shared word — the one the form flags for a look.
 */
export const RECIPE_LINE_MATCHES = ['exact', 'precedent', 'closest'];
/** What an operator hands the model: a name, and what kind of thing it is. */
export const adminFoodResearchRequestSchema = z.object({
    name: z.string().trim().min(2).max(160),
    kind: z.enum(FOOD_KINDS),
    /** A nudge — "Kerala style", "the packaged one". */
    hint: z.string().trim().max(200).optional(),
});
/**
 * An unsaved draft of a catalog row. Nothing here has been written, and there
 * is deliberately no endpoint that applies one: the operator reviews it in the
 * form, and the form they submit is what creates the row.
 *
 * Fields are nullable throughout — one the model could not answer, or one the
 * nutrition and portion bounds refused, comes back absent with a sentence in
 * `warnings`. That demotion is the move `scripts/audit-nutrition.ts` makes
 * when a proposed fix fails the guardrails: it becomes a flag rather than
 * being passed through, because a plausible wrong number arriving pre-filled
 * and unremarked is worse than no number.
 *
 * `allergenSource` is not here at all. See `ALLERGEN_WRITE_PROVENANCES`.
 */
export const adminFoodResearchDraftSchema = z.object({
    name: z.string(),
    kind: z.enum(FOOD_KINDS),
    category: z.string().nullable(),
    macrosPer100g: foodMacrosSchema.nullable(),
    dietClass: z.enum(FOOD_DIET_CLASSES).nullable(),
    hasOnionGarlic: z.boolean().nullable(),
    jainExcluded: z.boolean().nullable(),
    hasBeef: z.boolean().nullable(),
    /** A suggestion to read, never a claim — the form does not tick a provenance for these. */
    allergens: z.array(z.enum(ALLERGENS)),
    servableAsIs: z.boolean().nullable(),
    isCommonInIndia: z.boolean().nullable(),
    cuisines: z.array(z.enum(FOOD_CUISINES)),
    mealRole: z.enum(MEAL_ROLES).nullable(),
    prepTimeBand: z.enum(PREP_TIME_BANDS).nullable(),
    costBand: z.enum(COST_BANDS).nullable(),
    mealTimes: z.array(z.enum(FOOD_MEAL_TIMES)),
    seasonMonths: z.array(z.number().int()),
    servingPolicy: adminServingPolicySchema.nullable(),
    aliases: z.array(adminFoodAliasRequestSchema),
    portions: z.array(adminFoodPortionRequestSchema),
    /**
     * The model is never shown the catalog, so it returns names and grams only;
     * an id from it would be invented. The server resolves each name through
     * the ingredient matcher's ladder and says which tier answered, so the form
     * can show a nearest-name guess differently from an exact hit. `warnings`
     * names what it could not match and what it matched only by nearest name.
     */
    recipeLines: z.array(z.object({
        rawName: z.string(),
        ingredientFoodId: z.string().uuid().nullable(),
        /** The identity name of the matched row, for the form to display. */
        ingredientName: z.string().nullable(),
        /** Null when unmatched — and, on the form, once the operator picks by hand. */
        matchedBy: z.enum(RECIPE_LINE_MATCHES).nullable(),
        grams: z.number(),
        retentionGroup: z.string().nullable(),
    })),
    confidence: z.enum(RECIPE_CONFIDENCES).nullable(),
    /** The model's own account of where the figures come from. */
    note: z.string().nullable(),
    /** Why a field is missing, or what to check before trusting one. */
    warnings: z.array(z.string()),
    research: adminFoodResearchMetadataSchema.optional(),
});
/** The two views the catalog screen offers. Both are the same endpoint. */
export const FOOD_VIEWS = ['catalog', 'review'];
// --- Food requests -----------------------------------------------------------
/**
 * Foods the catalog answers badly, and what an operator did about each: ones
 * users looked for and could not find, and ones the system filed when a
 * typed meal, the coach or a photo met a gap.
 *
 * Its own screen rather than a third `FOOD_VIEWS` tab: those two are filter
 * presets over one endpoint, and a tab backed by a different endpoint would
 * quietly break that. It lives in this module all the same — the package's
 * `exports` map is hand-written with no wildcard, and a new file would cost a
 * package.json entry and a second contract test for one status tuple.
 *
 * Every approval writes through the catalog engine under the source
 * `food_request:<id>`, so it is in History with its sources and can be
 * reverted from there (`/admin/catalog/...`).
 */
export const FOOD_REQUEST_STATUSES = ['open', 'resolved', 'rejected'];
/**
 * What a request asks for: a food the catalog lacks (`new_food`), a word
 * people use for a catalog food that is none of its names (`name`, listed
 * once three people have used it), or a food whose facts need fixing, such as
 * a household unit it has no portion for (`fix`).
 */
export const FOOD_REQUEST_KINDS = ['new_food', 'name', 'fix'];
/** Who or what filed it: a person, a trainer, or a typed meal, the coach or a photo. */
export const FOOD_REQUEST_ORIGINS = ['user', 'trainer', 'typed_meal', 'coach', 'photo_scan'];
/** Where a request's AI research has got to. */
export const FOOD_REQUEST_DRAFT_STATUSES = ['none', 'queued', 'ready', 'failed'];
/**
 * Newest first (`recent`), the most people first (`impact`), or the ones
 * ready to approve first and then the most people (`ready`).
 */
export const FOOD_REQUEST_ORDERS = ['recent', 'impact', 'ready'];
/**
 * The fields a request's research proposes and a reviewer decides: the five
 * nutrition figures as one, the portion set as one, and a dish's recipe.
 */
export const FOOD_RESEARCH_FIELDS = ['nutrition', 'portions', 'recipe'];
export const adminFoodRequestSchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(FOOD_REQUEST_KINDS),
    origin: z.enum(FOOD_REQUEST_ORIGINS),
    /** As the user typed it, the dish an estimate was made under, or (NAME) the word people used. */
    name: z.string(),
    note: z.string().nullable(),
    status: z.enum(FOOD_REQUEST_STATUSES),
    /** Who asked, or whose meal filed it first. The console links it to the user detail screen. */
    userId: z.string().uuid(),
    /**
     * Their name and number, carried on the row the way a billing event carries
     * them. A triage queue that shows only a uuid makes the operator open every
     * request to find out whether the same person asked for six of them.
     */
    displayName: z.string().nullable(),
    phone: z.string(),
    /** How many different people asked or hit it. Always 1 for a person's own request. */
    userCount: z.number().int(),
    lastAskedAt: z.string(),
    /**
     * The food it is about: the AI estimate a new food was logged as (null when
     * it could not be estimated), or the catalog food a name or fix concerns.
     */
    foodId: z.string().uuid().nullable(),
    foodName: z.string().nullable(),
    /** `fix` only: the household unit asked for ("bowl"). */
    unit: z.string().nullable(),
    /**
     * The gym, when a trainer asked while planning a member's food (Steado for
     * Gyms): a request someone's plan is waiting on. Null for a member's own.
     */
    gymName: z.string().nullable(),
    /**
     * The catalog row an operator judged this to mean, and its name at the time
     * of reading. Null while open, and null again if that food is ever deleted —
     * the FK is SetNull, because the request outlives what it was pointed at.
     */
    resolvedFoodId: z.string().uuid().nullable(),
    resolvedFoodName: z.string().nullable(),
    rejectionReason: z.string().nullable(),
    answeredAt: z.string().nullable(),
    draftStatus: z.enum(FOOD_REQUEST_DRAFT_STATUSES),
    /**
     * How far the evidence carries what approving would write: the lowest
     * grade among the proposed fields the research graded. A field it could
     * not grade (no figures found) is counted in `toCheck`, not here. For
     * `name`, high when no other food answers to the word. Null when nothing
     * is graded. The queue and the review header both show this one.
     */
    draftConfidence: z.enum(RECIPE_CONFIDENCES).nullable(),
    /** High confidence and nothing for a reviewer to decide: one click approves it. */
    readyToApprove: z.boolean(),
    /**
     * How many proposed fields the reviewer must decide before approving (the
     * queue's "2 to check"): a fix's every field, a new food's below high
     * confidence or missing. 0 for a name, which has none. Null until the
     * research is in.
     */
    toCheck: z.number().int().nullable(),
    /** What approving would do, in a line: `Add "Tawa roti" as a name for Chapati`. */
    proposal: z.string(),
    researchedAt: z.string().nullable(),
    researchError: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const adminFoodRequestListQuerySchema = cursorPageQuerySchema.extend({
    status: z.enum(FOOD_REQUEST_STATUSES).optional(),
    kind: z.enum(FOOD_REQUEST_KINDS).optional(),
    origin: z.enum(FOOD_REQUEST_ORIGINS).optional(),
    /** `queued` is the console's "Being researched". */
    draftStatus: z.enum(FOOD_REQUEST_DRAFT_STATUSES).optional(),
    confidence: z.enum(RECIPE_CONFIDENCES).optional(),
    /** Only requests at least this many people asked for or hit. */
    minUsers: z.coerce.number().int().min(1).max(10_000).optional(),
    /** What people typed: the request's name, or any ask's words. */
    q: z.string().trim().min(1).max(80).optional(),
    order: z.enum(FOOD_REQUEST_ORDERS).default('recent'),
});
export const adminFoodRequestParamsSchema = z.object({ id: z.string().uuid() });
/** The queue's header and its kind tabs. */
export const adminFoodRequestSummarySchema = z.object({
    open: z.number().int(),
    openByKind: z.object({ new_food: z.number().int(), name: z.number().int(), fix: z.number().int() }),
    readyToApprove: z.number().int(),
    /** Open requests whose research is queued or running. */
    researching: z.number().int(),
    oldestOpenAt: z.string().nullable(),
    /** Meal log items on AI estimates nobody has checked: what approving new foods puts right. */
    logsOnEstimates: z.number().int(),
});
/**
 * One source the research read. Kept on the request until 90 days after it is
 * answered; the page itself never is.
 */
export const adminFoodRequestSourceSchema = z.object({
    /** Null for a row of Steado's own catalog. */
    url: z.string().nullable(),
    title: z.string().nullable(),
    /** The draft fields that cite it and survived the evidence checks. */
    supports: z.array(z.enum(FOOD_RESEARCH_FIELDS)),
    foodState: z.enum(['raw', 'cooked', 'as_sold', 'unknown']),
    /** At most 200 characters of the source: the lines stating the figures the draft took from it. */
    excerpt: z.string(),
});
const requestPortionSchema = z.object({
    label: z.string(),
    unit: z.enum(PORTION_UNITS),
    grams: z.number(),
    isDefault: z.boolean(),
});
/** One side of what a request would change: the food as it is, or as the research proposes. */
export const adminFoodRequestFactsSchema = z.object({
    name: z.string().nullable(),
    aliases: z.array(z.string()),
    macrosPer100g: foodMacrosSchema.nullable(),
    portions: z.array(requestPortionSchema),
});
/**
 * One field the research proposes. `needsDecision` is the reviewer's to
 * settle: an approval must carry a value (or `keep`) for every such field,
 * and nothing is chosen for them. A fix's every proposed field needs one; a
 * new food's, those below high confidence.
 */
export const adminFoodRequestFieldSchema = z.object({
    field: z.enum(FOOD_RESEARCH_FIELDS),
    confidence: z.enum(RECIPE_CONFIDENCES).nullable(),
    needsDecision: z.boolean(),
    /** The research's own account of the field: its basis and assumptions. */
    note: z.string().nullable(),
    /** Indexes into the detail's `sources`. */
    sources: z.array(z.number().int().nonnegative()),
});
/**
 * The request's draft. `food` is the researched row in the create form's
 * shape; `current` and `proposed` say what approving would change: the
 * estimate's numbers (new food) or the food's (fix) against the researched
 * ones. `reviewerNotes` are the research's warnings and assumptions.
 */
export const adminFoodRequestDraftSchema = z.object({
    food: adminFoodResearchDraftSchema.nullable(),
    current: adminFoodRequestFactsSchema.nullable(),
    proposed: adminFoodRequestFactsSchema.nullable(),
    fields: z.array(adminFoodRequestFieldSchema),
    reviewerNotes: z.array(z.string()),
});
/** The food a request is about, as it is now. */
export const adminFoodRequestFoodSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    displayName: z.string().nullable(),
    kind: z.enum(FOOD_KINDS),
    source: z.enum(FOOD_SOURCES),
    isVerified: z.boolean(),
    isActive: z.boolean(),
    macrosPer100g: foodMacrosSchema,
    aliases: z.array(z.string()),
    portions: z.array(requestPortionSchema),
});
/** A catalog food that may already be what the request asks for. */
export const adminFoodRequestCandidateSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    source: z.enum(FOOD_SOURCES),
    isVerified: z.boolean(),
    macrosPer100g: foodMacrosSchema,
    defaultPortion: z.object({ label: z.string(), grams: z.number() }).nullable(),
    /** Name likeness, 0 to 1. */
    similarity: z.number(),
});
/**
 * The meals logged on the AI estimate a new food request is about, and what
 * each would count once the request is approved (`kcalAfter`, at the draft's
 * proposed figures; null without them). Approving moves them to the food it
 * answers with, at its figures, and they lose the "Estimated by AI" mark.
 */
export const adminFoodRequestEstimateLogsSchema = z.object({
    count: z.number().int(),
    people: z.number().int(),
    grams: z.number(),
    kcalNow: z.number(),
    kcalAfter: z.number().nullable(),
    /** The newest 20. */
    recent: z.array(z.object({
        itemId: z.string().uuid(),
        userId: z.string().uuid(),
        localDate: z.string(),
        mealType: z.string(),
        saidName: z.string().nullable(),
        portionLabel: z.string().nullable(),
        grams: z.number(),
        kcalNow: z.number(),
        kcalAfter: z.number().nullable(),
    })),
});
export const adminFoodRequestDetailSchema = adminFoodRequestSchema.extend({
    /** Each person behind it, in their words, newest first (at most 50). */
    asks: z.array(z.object({
        userId: z.string().uuid(),
        displayName: z.string().nullable(),
        said: z.string(),
        createdAt: z.string(),
    })),
    /** `name`: the words people used for it, and how many people used each. */
    spellings: z.array(z.object({ name: z.string(), people: z.number().int() })),
    draft: adminFoodRequestDraftSchema.nullable(),
    sources: z.array(adminFoodRequestSourceSchema),
    /** The operator's note the last research ran with. */
    draftHint: z.string().nullable(),
    draftModel: z.string().nullable(),
    food: adminFoodRequestFoodSchema.nullable(),
    /** `new_food`: catalog foods with a name like it, closest first. */
    candidates: z.array(adminFoodRequestCandidateSchema),
    /** `new_food` with an estimate: the meals logged on it. */
    estimateLogs: adminFoodRequestEstimateLogsSchema.nullable(),
    /**
     * The food's fields changed since the draft was made (a fix), newest first:
     * approving waits until the reviewer has compared (`base`).
     */
    changedSinceDraft: z.array(z.object({ field: z.string(), changedAt: z.string(), source: z.string() })),
    /** `name`: other foods that already answer to the word. */
    clashes: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
    /** The food's reach: its logs and the people behind them in 90 days, and its hearts. */
    touches: z
        .object({ logs90d: z.number().int(), people90d: z.number().int(), favourites: z.number().int() })
        .nullable(),
    /**
     * `new_food`, once approved: what its latest approval (`batchId`) moved off
     * the AI estimate it retired, and how many of those meals a revert has moved
     * back (a meal edited since stays on the food). Null when it moved nothing.
     */
    estimateMoves: z
        .object({
        batchId: z.string().uuid(),
        meals: z.number().int(),
        mealsMovedBack: z.number().int(),
        favourites: z.number().int(),
        recipeLines: z.number().int(),
    })
        .nullable(),
});
/** Per 100 g, held to the columns' own bounds; the nutrition rules bind at the write. */
const decidedMacrosSchema = z.object({
    kcal: z.number().nonnegative().max(2000),
    proteinG: z.number().nonnegative().max(100),
    carbsG: z.number().nonnegative().max(100),
    fatG: z.number().nonnegative().max(100),
    fibreG: z.number().nonnegative().max(100).nullable(),
});
/** `keep` leaves the field as the food has it; `value` is the reviewer's chosen value. */
function decision(value) {
    return z.union([z.object({ keep: z.literal(true) }).strict(), z.object({ value }).strict()]);
}
/**
 * The reviewer's choice for each field, nothing pre-selected: every field the
 * draft marks `needsDecision` must be here, or the approval is refused with
 * the ones left. A field sent that the draft did not flag overrides it.
 */
export const adminFoodRequestDecisionsSchema = z
    .object({
    nutrition: decision(decidedMacrosSchema).optional(),
    portions: decision(z.array(adminFoodPortionRequestSchema).min(1).max(4)).optional(),
    recipe: decision(z.array(adminFoodRecipeLineRequestSchema).min(1).max(40)).optional(),
})
    .strict();
/**
 * Creates the food a `new_food` request asks for from its reviewed draft, as
 * a dish, and folds the AI estimate it was logged as into it: the estimate's
 * names become the dish's, its meals move to the dish at the dish's figures.
 * `note` says where the figures come from; a new dish must cite a URL, which
 * the research's sources give unless the reviewer typed their own values.
 */
export const adminFoodRequestCreateFoodRequestSchema = z.object({
    name: z.string().trim().min(2).max(160).optional(),
    decisions: adminFoodRequestDecisionsSchema,
    note: z.string().trim().min(1).max(2000).optional(),
});
/**
 * Answers a `name` request: the word as another name of the food, or as the
 * name every card shows (`display_name`). Nothing is chosen for the reviewer.
 * `alsoNames` adds other spellings people used.
 */
export const adminFoodRequestApproveNameRequestSchema = z.object({
    as: z.enum(['alias', 'display_name']),
    name: z.string().trim().min(2).max(160).optional(),
    alsoNames: z.array(z.string().trim().min(2).max(160)).max(5).default([]),
});
/**
 * Applies a `fix`: the reviewer's decision on each proposed field. `base` is
 * when they last compared with the food as it is now; a field changed after
 * it is a conflict (409) and nothing is written. Defaults to when the draft
 * was made.
 */
export const adminFoodRequestApproveFixRequestSchema = z.object({
    decisions: adminFoodRequestDecisionsSchema,
    base: z.string().datetime().optional(),
});
/** Keeps a `new_food` request's AI estimate as it is, marked reviewed. Says why. */
export const adminFoodRequestKeepEstimateRequestSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
/** What an approval wrote: the food, and its changes in History under `batchId`. */
export const adminFoodRequestApprovalSchema = z.object({
    request: adminFoodRequestSchema,
    foodId: z.string().uuid(),
    /** The History batch; `POST /admin/catalog/batches/{batchId}/revert` undoes it. */
    batchId: z.string().uuid(),
    changes: z.number().int(),
    /** Meals moved from the estimate to the food, at its figures. */
    logsMoved: z.number().int(),
});
/** Several `name` requests at once, each as another name of its food. */
export const adminFoodRequestBulkNamesRequestSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(50),
});
export const adminFoodRequestBulkNamesResultSchema = z.object({
    added: z.array(z.object({
        id: z.string().uuid(),
        foodId: z.string().uuid(),
        name: z.string(),
        batchId: z.string().uuid(),
    })),
    /** Each with why: not an open name request, a clash, or what the engine refused. */
    refused: z.array(z.object({ id: z.string().uuid(), reason: z.string() })),
});
/**
 * Points the request at the catalog food that answers it. In particular it
 * writes **no** preference row for the user: an operator is saying "this is
 * the food you meant", not making a claim on somebody's behalf about whether
 * they want to eat it. A `new_food` request's AI estimate is folded into that
 * food, as `create-food` folds it into a new one.
 */
export const adminFoodRequestResolveRequestSchema = z.object({ foodId: z.string().uuid() });
/** Runs the AI research again, with an operator's note to steer it. */
export const adminFoodRequestResearchRequestSchema = z.object({
    note: z.string().trim().min(1).max(200).optional(),
});
/** Declining. The reason is required, and stored on the row as well as on the audit entry. */
export const adminFoodRequestRejectRequestSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
//# sourceMappingURL=foods.js.map